import React, { useEffect, useState } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonText,
} from "@ionic/react";

interface PatternQuestion {
    pattern: (string | null)[];
    options: string[];
    correctAnswer: string;
    missingIndex: number;
    type: "number" | "alphabet" | "growing" | "repeating" | "figure";
    subtype?: "rectangle" | "circle" | "square"
}

type Logic =
    | { type: "add"; step: number }
    | { type: "subtract"; step: number }
    | { type: "multiply"; step: number }
    | { type: "alt-add-sub"; step: number };

const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

const getAlphabet = (index: number): string => {
    const charCode = "A".charCodeAt(0) + index;
    return String.fromCharCode(charCode);
};

const emojiOptions = ["😀", "🎈", "🐶", "⭐", "🍎", "🚗", "🌸", "🍩", "🐠", "🍕"];

const patternBackground = [
    "duckling.png",
    "hat.png",
    "monkey.png",
    "goldfish.png",
    "butterfly.png",
    "sun.png",
];

const AutoPatternGenerator: React.FC = () => {
    const [question, setQuestion] = useState<PatternQuestion | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [background, setBackground] = useState("duckling.png");
    const [xCord, setXCord] = useState(30);
    const [yCord, setYCord] = useState(30);

    const createNewQuestion = () => {
        const patternType = getRandomInt(0, 4);
        const q = 
        patternType === 0
            ? generateNumberPattern()
            : patternType === 1
            ? generateAlphabetPattern()
            : patternType === 2
            ? generateGrowingPattern()
            : patternType === 3
            ? generateRepeatingPattern()
            : generateFigurePattern();
        setQuestion(q);
        setSelected(null);
    };

    const handleAnswer = (value: string) => {
        if (!question) return;
        setSelected(value);
        if (value === question.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                setBackground(
                    patternBackground[getRandomInt(0, patternBackground.length - 1)]
                );
                createNewQuestion();
            }, 3000);
        }
    };

    const generateNumberPattern = (): PatternQuestion => {
        const start = getRandomInt(1, 10);
        const length = 5;
        const logics: Logic[] = [
            { type: "add", step: getRandomInt(1, 5) },
            { type: "subtract", step: getRandomInt(1, 5) },
            { type: "multiply", step: Math.random() < 0.5 ? 2 : 3 },
            { type: "alt-add-sub", step: getRandomInt(1, 4) },
        ];
        const logic = logics[getRandomInt(0, logics.length - 1)];

        const pattern: number[] = [start];
        let current = start;

        for (let i = 1; i < length; i++) {
            const step = logic.step;
            switch (logic.type) {
                case "add":
                    current += step;
                    break;
                case "subtract":
                    current -= step;
                    break;
                case "multiply":
                    current *= step;
                    break;
                case "alt-add-sub":
                    current = i % 2 === 0 ? current - step : current + step;
                    break;
            }
            pattern.push(current);
        }

        const missingIndex = getRandomInt(0, pattern.length - 1);
        const correctAnswer = pattern[missingIndex];
        const displayPattern: (string | null)[] = pattern.map((num, i) =>
            i === missingIndex ? null : String(num)
        );

        const options = new Set<number>();
        options.add(correctAnswer);
        while (options.size < 4) {
            const distractor = correctAnswer + getRandomInt(-5, 5);
            if (distractor !== correctAnswer && distractor >= 0) {
                options.add(distractor);
            }
        }

        return {
            pattern: displayPattern,
            options: Array.from(options).map(String).sort(() => Math.random() - 0.5),
            correctAnswer: String(correctAnswer),
            missingIndex,
            type: "number",
        };
    };

    const generateAlphabetPattern = (): PatternQuestion => {
        const startIndex = getRandomInt(0, 10);
        const step = getRandomInt(1, 3);
        const length = 5;
        const pattern: string[] = [];

        for (let i = 0; i < length; i++) {
            pattern.push(getAlphabet(startIndex + i * step));
        }

        const missingIndex = getRandomInt(0, pattern.length - 1);
        const correctAnswer = pattern[missingIndex];
        const displayPattern: (string | null)[] = pattern.map((char, i) =>
            i === missingIndex ? null : char
        );

        const options = new Set<string>();
        options.add(correctAnswer);
        while (options.size < 4) {
            const distractorIndex = getRandomInt(0, 25);
            const distractor = getAlphabet(distractorIndex);
            if (!options.has(distractor)) {
                options.add(distractor);
            }
        }

        return {
            pattern: displayPattern,
            options: Array.from(options).sort(() => Math.random() - 0.5),
            correctAnswer,
            missingIndex,
            type: "alphabet",
        };
    };

    const generateGrowingPattern = (): PatternQuestion => {
        const baseEmoji = emojiOptions[getRandomInt(0, emojiOptions.length - 1)];
        const length = 5;
        const pattern: string[] = [];

        for (let i = 1; i <= length; i++) {
            pattern.push(baseEmoji.repeat(i));
        }

        const missingIndex = getRandomInt(0, length - 1);
        const correctAnswer = pattern[missingIndex];
        const displayPattern: (string | null)[] = pattern.map((val, i) =>
            i === missingIndex ? null : val
        );

        const options = new Set<string>();
        options.add(correctAnswer);
        while (options.size < 4) {
            const distractLength = getRandomInt(1, length + 1);
            const distractor = baseEmoji.repeat(distractLength);
            if (!options.has(distractor)) {
                options.add(distractor);
            }
        }

        return {
            pattern: displayPattern,
            options: Array.from(options).sort(() => Math.random() - 0.5),
            correctAnswer,
            missingIndex,
            type: "growing",
        };
    };

    const generateRepeatingPattern = (): PatternQuestion => {
        const unitLength = getRandomInt(2, 4);
        const patternUnit = Array.from({ length: unitLength }, () =>
            patternBackground[getRandomInt(0, patternBackground.length - 1)]
        );

        const pattern: string[] = [];
        const totalLength = 12;

        for (let i = 0; i < totalLength; i++) {
            pattern.push(patternUnit[i % unitLength]);
        }

        const missingIndex = getRandomInt(0, totalLength - 1);
        const correctAnswer = pattern[missingIndex];
        const displayPattern = pattern.map((img, i) => (i === missingIndex ? null : img));

        const options = new Set<string>();
        options.add(correctAnswer);
        while (options.size < 4) {
            const distractor = patternBackground[getRandomInt(0, patternBackground.length - 1)];
            if (!options.has(distractor)) {
                options.add(distractor);
            }
        }

        return {
            pattern: displayPattern,
            options: Array.from(options).sort(() => Math.random() - 0.5),
            correctAnswer,
            missingIndex,
            type: "repeating",
        };
    };



    const generateFigurePattern = (): PatternQuestion => {
        const colors = [
            "#FFB6C1", // Light Pink
            "#ADD8E6", // Light Blue
            "#90EE90", // Light Green
            "#FFD700", // Gold

            // 🌸 Pastel & Floral
            "#FFA07A", // Light Salmon
            "#E6E6FA", // Lavender
            "#F08080", // Light Coral
            "#B0E0E6", // Powder Blue
            "#FF69B4", // Hot Pink
            "#98FB98", // Pale Green
            "#FFDEAD", // Navajo White
            "#FFFACD", // Lemon Chiffon
            "#DDA0DD", // Plum
            "#E0FFFF", // Light Cyan
            "#FFE4E1", // Misty Rose
            "#F5DEB3", // Wheat
            "#F0E68C", // Khaki
            "#AFEEEE", // Pale Turquoise
            "#F5F5DC", // Beige
            "#C1FFC1", // Light Mint
        ];

        const strokeColors = ["#000", "#555", "#333"];

        // Randomly choose between rectangle (0) and circle (1)
        const shapeTypes = ["rectangle", "circle", "square"];
        const shapeType = shapeTypes[getRandomInt(0, 2)];
        let pattern: any = [];

        if (shapeType == "rectangle") {

            const createBox = (fill: string, stroke: string, styleType: number): string => {
                let lines = "";
                switch (styleType) {
                    case 1:
                        lines = `
                        <line x1="0" y1="26" x2="75" y2="26" stroke="${stroke}" stroke-width="2" />
                        <line x1="0" y1="32" x2="75" y2="32" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 2:
                        lines = `
                        <line x1="0" y1="0" x2="75" y2="60" stroke="${stroke}" stroke-width="2" />
                        <line x1="0" y1="60" x2="75" y2="0" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 3:
                        lines = `
                        <line x1="37" y1="0" x2="37" y2="60" stroke="${stroke}" stroke-width="2" />
                        <line x1="45" y1="0" x2="45" y2="60" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 4:
                        lines = `
                        <polyline points="0,10 20,30 40,10 60,30 75,10" fill="none" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 5:
                        lines = `
                        <circle cx="18" cy="30" r="5" fill="${stroke}" />
                        <circle cx="37" cy="30" r="5" fill="${stroke}" />
                        <circle cx="56" cy="30" r="5" fill="${stroke}" />
                    `;
                        break;
                    case 6:
                        lines = `
                        <rect x="10" y="10" width="15" height="15" fill="${stroke}" />
                        <rect x="30" y="10" width="15" height="15" fill="${stroke}" />
                        <rect x="50" y="10" width="15" height="15" fill="${stroke}" />
                    `;
                        break;
                    case 7:
                        lines = `
                        <line x1="0" y1="0" x2="75" y2="60" stroke="${stroke}" stroke-width="2" />
                        <line x1="0" y1="60" x2="75" y2="0" stroke="${stroke}" stroke-width="2" />
                        <line x1="0" y1="30" x2="75" y2="30" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 8:
                        lines = `
                        <polygon points="20,10 55,10 37,50" fill="none" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 9:
                        lines = `
                        <line x1="0" y1="20" x2="75" y2="20" stroke="${stroke}" stroke-width="2" />
                        <line x1="0" y1="40" x2="75" y2="40" stroke="${stroke}" stroke-width="2" />
                        <line x1="37" y1="0" x2="37" y2="60" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 10:
                        lines = `
                        <circle cx="37" cy="30" r="20" fill="none" stroke="${stroke}" stroke-width="2" />
                        <line x1="37" y1="10" x2="37" y2="50" stroke="${stroke}" stroke-width="2" />
                        <line x1="17" y1="30" x2="57" y2="30" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;
                    case 11:
                        lines = `
                            <path d="M0,30 Q18,0 37,30 Q56,60 75,30" fill="none" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 12:
                        lines = `
                            <path d="M0,30 L10,20 L20,40 L30,20 L40,40 L50,20 L60,40 L75,30" fill="none" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 13:
                        lines = `
                            <line x1="0" y1="0" x2="75" y2="60" stroke="${stroke}" stroke-width="2" stroke-dasharray="5,3" />
                            <line x1="0" y1="60" x2="75" y2="0" stroke="${stroke}" stroke-width="2" stroke-dasharray="5,3" />
                        `;
                        break;
                    case 14:
                        lines = `
                            <ellipse cx="37" cy="30" rx="30" ry="20" fill="none" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 15:
                        lines = `
                            <line x1="0" y1="10" x2="75" y2="10" stroke="${stroke}" stroke-width="2" />
                            <line x1="0" y1="25" x2="75" y2="25" stroke="${stroke}" stroke-width="2" />
                            <line x1="0" y1="40" x2="75" y2="40" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 16:
                        lines = `
                            <circle cx="20" cy="20" r="5" fill="${stroke}" />
                            <circle cx="37" cy="30" r="5" fill="${stroke}" />
                            <circle cx="55" cy="40" r="5" fill="${stroke}" />
                        `;
                        break;
                    case 17:
                        lines = `
                            <polygon points="0,0 75,0 37,60" fill="none" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 18:
                        lines = `
                            <line x1="0" y1="30" x2="75" y2="30" stroke="${stroke}" stroke-width="2" />
                            <line x1="37" y1="0" x2="37" y2="60" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 19:
                        lines = `
                            <rect x="5" y="5" width="65" height="50" fill="none" stroke="${stroke}" stroke-width="2" />
                            <line x1="0" y1="0" x2="75" y2="60" stroke="${stroke}" stroke-width="1" stroke-dasharray="4,2" />
                        `;
                        break;
                    case 20:
                        lines = `
                            <polyline points="0,30 15,10 30,50 45,10 60,50 75,30" fill="none" stroke="${stroke}" stroke-width="2" />
                        `;
                        break;
                    case 21:
                        lines = `
                            <circle cx="37" cy="30" r="8" fill="${stroke}" />
                            <circle cx="25" cy="30" r="6" fill="${stroke}" />
                            <circle cx="49" cy="30" r="6" fill="${stroke}" />
                            <circle cx="37" cy="18" r="6" fill="${stroke}" />
                            <circle cx="37" cy="42" r="6" fill="${stroke}" />
                        `;
                        break;
                    case 22:
                        lines = `
                        <circle cx="37" cy="30" r="6" fill="${stroke}" />
                        ${[0, 60, 120, 180, 240, 300].map(angle => {
                            const rad = angle * Math.PI / 180;
                            const cx = 37 + 12 * Math.cos(rad);
                            const cy = 30 + 12 * Math.sin(rad);
                            return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="5" fill="${stroke}" />`;
                        }).join('\n')}
                    `;
                        break;
                    case 23:
                        lines = `
                        ${[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
                            const rad = angle * Math.PI / 180;
                            const x2 = 37 + 20 * Math.cos(rad);
                            const y2 = 30 + 20 * Math.sin(rad);
                            return `<line x1="37" y1="30" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${stroke}" stroke-width="2" />`;
                        }).join('\n')}
                    `;
                        break;

                    case 24:
                        lines = `
                        <path d="M30,40 Q37,20 44,40 Q37,35 30,40 Z" fill="${stroke}" />
                        <line x1="37" y1="40" x2="37" y2="60" stroke="${stroke}" stroke-width="2" />
                    `;
                        break;

                    case 25:
                        lines = `
                        <circle cx="37" cy="30" r="4" fill="${stroke}" />
                        ${[...Array(10)].map((_, i) => {
                            const angle = i * 36 * Math.PI / 180;
                            const cx = 37 + 15 * Math.cos(angle);
                            const cy = 30 + 15 * Math.sin(angle);
                            return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="3" fill="${stroke}" />`;
                        }).join('\n')}
                    `;
                        break;



                }

                return `<svg width="75" height="60">
                    <rect width="75" height="60" fill="${fill}" stroke="${stroke}" stroke-width="3" />
                    ${lines}
                </svg>`;
            };


            const fillA = colors[getRandomInt(0, colors.length - 1)];
            const strokeA = strokeColors[getRandomInt(0, strokeColors.length - 1)];
            const styleA = getRandomInt(1, 25);
            const boxA = createBox(fillA, strokeA, styleA);

            const fillB = colors[getRandomInt(0, colors.length - 1)];
            const strokeB = strokeColors[getRandomInt(0, strokeColors.length - 1)];
            const styleB = getRandomInt(1, 25);
            const boxB = createBox(fillB, strokeB, styleB);

            pattern = [boxA, boxB, boxB, boxA];

            const missingIndex = getRandomInt(0, 3);
            const correctAnswer = pattern[missingIndex];
            const displayPattern = pattern.map((val: any, i: number) => (i === missingIndex ? null : val));

            // Build options: ensure uniqueness and correctAnswer only appears once
            const options: string[] = [correctAnswer];

            while (options.length < 4) {
                const newFill = colors[getRandomInt(0, colors.length - 1)];
                const newStroke = strokeColors[getRandomInt(0, strokeColors.length - 1)];
                const newStyle = getRandomInt(1, 4);

                const newOption = createBox(newFill, newStroke, newStyle);


                // Avoid duplicates by comparing with all existing options
                const isDuplicate = options.some(opt => opt === newOption);
                if (!isDuplicate) {
                    options.push(newOption);
                }
            }

            return {
                pattern: displayPattern,
                options: options.sort(() => Math.random() - 0.5),
                correctAnswer,
                missingIndex,
                type: "figure",
                subtype: "rectangle"
            };

        }
        else if (shapeType == "square") {

            const patternStyle = ["linesA", "dotB", "diagonalC", "crossD", "zigzag", "grid", "waves", "stripes-diagonal", "crosshatch", "checker", "circle-dots", "lines-horizontal", "triangles", "dots-small", "multiGradient", "colorStripes"];
            const styleA = patternStyle[getRandomInt(0, 14)];
            const styleB = patternStyle[getRandomInt(0, 14)];


            const quaterPath = ["60,60 60,0 120,60", "60,60 120,60 60,120", "60,60 0,60 60,0", "60,60 60,120 0,60"];

            const patternBox = (path: string, patternStyle: string, strokeColor: string, strokeWidth: number) => {
                return `<polygon points="${path}" fill="url(#${patternStyle})" stroke="#${strokeColor}" strokeWidth="${strokeWidth}" />`;
            }

            const questionPatternArray = [styleA, styleB, styleB, styleA];

            const quaterA = patternBox(quaterPath[0], styleA, '333', 1);
            const quaterB = patternBox(quaterPath[1], styleB, '333', 1);
            const quaterC = patternBox(quaterPath[2], styleB, '333', 1);
            const quaterD = patternBox(quaterPath[3], styleA, '333', 1);
            pattern = [quaterA, quaterB, quaterC, quaterD];

            const missingIndex = getRandomInt(0, 3);
            const correctAnswer = pattern[missingIndex];
            const displayPattern = pattern.map((val: any, i: number) => (i === missingIndex ? null : val));

            if (missingIndex == 0) {
                setXCord(90);
                setYCord(30);
            }
            else if(missingIndex == 1) {
                setXCord(90);
                setYCord(90);
            }
            else if(missingIndex == 3) {
                setXCord(30);
                setYCord(90);
            }
            else {
                setXCord(30);
                setYCord(30);
            }

            // Build options: ensure uniqueness and correctAnswer only appears once
            const options: string[] = [correctAnswer];
            // const newOption = patternBox(quaterPath[missingIndex], questionPatternArray[missingIndex], '333', 1);
            // options.push(newOption);

            while (options.length < 4) {
                const newOption = patternBox(quaterPath[missingIndex], patternStyle[getRandomInt(0, 14)], '333', 1);
                // Avoid duplicates by comparing with all existing options
                const isDuplicate = options.some(opt => opt === newOption);
                if (!isDuplicate) {
                    options.push(newOption);
                }
            }

            return {
                pattern: displayPattern,
                options: options.sort(() => Math.random() - 0.5),
                correctAnswer,
                missingIndex,
                type: "figure",
                subtype: "square"
            };
        }
        else {
            const patternStyle = ["linesA", "dotB", "diagonalC", "crossD", "zigzag", "grid", "waves", "stripes-diagonal", "crosshatch", "checker", "circle-dots", "lines-horizontal", "triangles", "dots-small", "multiGradient", "colorStripes"];
            const styleA = patternStyle[getRandomInt(0, 14)];
            const styleB = patternStyle[getRandomInt(0, 14)];


            const quaterPath = ["M60,60 L60,0 A60,60 0 0 0 0,60 Z", "M60,60 L120,60 A60,60 0 0 0 60,0 Z", "M60,60 L0,60 A60,60 0 0 0 60,120 Z", "M60,60 L60,120 A60,60 0 0 0 120,60 Z"];

            const patternBox = (path: string, patternStyle: string, strokeColor: string, strokeWidth: number) => {
                return `<path d="${path}" fill="url(#${patternStyle})" stroke="#${strokeColor}" strokeWidth="${strokeWidth}" />`;
            }

            const questionPatternArray = [styleA, styleB, styleB, styleA];

            const quaterA = patternBox(quaterPath[0], styleA, '333', 1);
            const quaterB = patternBox(quaterPath[1], styleB, '333', 1);
            const quaterC = patternBox(quaterPath[2], styleB, '333', 1);
            const quaterD = patternBox(quaterPath[3], styleA, '333', 1);
            pattern = [quaterA, quaterB, quaterC, quaterD];

            const missingIndex = getRandomInt(0, 3);
            const correctAnswer = pattern[missingIndex];
            const displayPattern = pattern.map((val: any, i: number) => (i === missingIndex ? null : val));

            if (missingIndex == 0) {
                setXCord(30);
                setYCord(30);
            }
            else if(missingIndex == 1) {
                setXCord(90);
                setYCord(30);
            }
            else if(missingIndex == 2) {
                setXCord(30);
                setYCord(90);
            }
            else {
                setXCord(90);
                setYCord(90);
            }
            // Build options: ensure uniqueness and correctAnswer only appears once
            const options: string[] = [correctAnswer];
            // const newOption = patternBox(quaterPath[missingIndex], questionPatternArray[missingIndex], '333', 1);
            // options.push(newOption);

            while (options.length < 4) {
                const newOption = patternBox(quaterPath[missingIndex], patternStyle[getRandomInt(0, 14)], '333', 1);
                // Avoid duplicates by comparing with all existing options
                const isDuplicate = options.some(opt => opt === newOption);
                if (!isDuplicate) {
                    options.push(newOption);
                }
            }

            return {
                pattern: displayPattern,
                options: options.sort(() => Math.random() - 0.5),
                correctAnswer,
                missingIndex,
                type: "figure",
                subtype: "circle"
            };
        }

    };

    useEffect(() => {
        createNewQuestion();
        setBackground(
            patternBackground[getRandomInt(0, patternBackground.length - 1)]
        );
        document.title =
            "Mamas Guidance - OLYMPIAD - Class One - Logical Reasoning - Patterns";
    }, []);

    return (
        <>
            {!showImage ? (
                <div className="contentPart showCenter fillPreposition additionWord">
                    {question && (
                        <>
                            <IonText>
                                <div className="" style={{ fontSize: "1.2rem", marginBottom: "20px" }}>
                                    {question.type != "figure" && question.pattern.map((val, idx) => (
                                        <div key={idx} className="image-container">
                                            {(question.type === "growing" ||  question.type === "alphabet")? (
                                                <span className="font-50">
                                                    {val === null ? "?" : val}
                                                </span>
                                            ) : question.type === "repeating" ? (
                                                <img className="repeating"
                                                    src={`${process.env.REACT_APP_API_URL ?? ""}${val ?? "question-mark.png"}`}
                                                    alt="pattern-img"
                                                />
                                            )
                                                : question.type === "number" ? (
                                                    <>
                                                        <img
                                                            src={`${process.env.REACT_APP_API_URL ?? ""}${background}`}
                                                            style={{ width: "80px" }}
                                                            alt="pattern-img"
                                                        />
                                                        <span className="patternNumberText">
                                                            {val === null ? "?" : val}
                                                        </span>
                                                    </>
                                                ) : <></>}

                                        </div>
                                    ))}
                                    {question.type === "figure" ? (
                                        <>
                                            {question.subtype === "rectangle" ? (
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 75px)", justifyContent: "center" }}>
                                                    {question.pattern.map((val, index) => (
                                                        <div key={index} className={`image-container ${val ? '' : 'questionFigure'}`}>
                                                            <span dangerouslySetInnerHTML={{ __html: val || "?" }} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <>
                                                    <svg width="120" height="120">
                                                        <defs>
                                                            <pattern id="linesA" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="6" fill="#ADD8E6" />
                                                                <line x1="0" y1="0" x2="6" y2="0" stroke="#333" strokeWidth="1" />
                                                            </pattern>
                                                            <pattern id="dotsB" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="6" fill="#FFB6C1" />
                                                                <circle cx="3" cy="3" r="1" fill="#333" />
                                                            </pattern>
                                                            <pattern id="diagonalC" width="8" height="8" patternUnits="userSpaceOnUse">
                                                                <rect width="8" height="8" fill="#FFB6C1" />
                                                                <line x1="0" y1="8" x2="8" y2="0" stroke="#333" strokeWidth="1" />
                                                            </pattern>
                                                            <pattern id="crossD" width="8" height="8" patternUnits="userSpaceOnUse">
                                                                <rect width="8" height="8" fill="#ADD8E6" />
                                                                <line x1="0" y1="0" x2="8" y2="8" stroke="#333" strokeWidth="1" />
                                                                <line x1="8" y1="0" x2="0" y2="8" stroke="#333" strokeWidth="1" />
                                                            </pattern>
                                                            <pattern id="zigzag" width="8" height="8" patternUnits="userSpaceOnUse">
                                                                <rect width="8" height="8" fill="#FFE4B5" />
                                                                <polyline points="0,4 2,2 4,6 6,2 8,4" stroke="#333" strokeWidth="1" fill="none" />
                                                            </pattern>

                                                            <pattern id="grid" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="6" fill="#98FB98" />
                                                                <path d="M 0 0 L 0 6 M 0 0 L 6 0" stroke="#333" strokeWidth="0.5" />
                                                            </pattern>

                                                            <pattern id="waves" width="10" height="10" patternUnits="userSpaceOnUse">
                                                                <rect width="10" height="10" fill="#E6E6FA" />
                                                                <path d="M0,5 Q2.5,0 5,5 Q7.5,10 10,5" stroke="#333" strokeWidth="1" fill="none" />
                                                            </pattern>

                                                            <pattern id="stripes-diagonal" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="6" fill="#FFDAB9" />
                                                                <line x1="0" y1="6" x2="6" y2="0" stroke="#333" strokeWidth="1" />
                                                            </pattern>

                                                            <pattern id="crosshatch" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="6" fill="#E0FFFF" />
                                                                <line x1="0" y1="0" x2="6" y2="0" stroke="#333" strokeWidth="0.5" />
                                                                <line x1="0" y1="0" x2="0" y2="6" stroke="#333" strokeWidth="0.5" />
                                                            </pattern>

                                                            <pattern id="checker" width="8" height="8" patternUnits="userSpaceOnUse">
                                                                <rect width="8" height="8" fill="#FFFACD" />
                                                                <rect x="0" y="0" width="4" height="4" fill="#333" />
                                                                <rect x="4" y="4" width="4" height="4" fill="#333" />
                                                            </pattern>

                                                            <pattern id="circle-dots" width="8" height="8" patternUnits="userSpaceOnUse">
                                                                <rect width="8" height="8" fill="#F0E68C" />
                                                                <circle cx="4" cy="4" r="1" fill="#333" />
                                                            </pattern>

                                                            <pattern id="lines-horizontal" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="6" fill="#F5DEB3" />
                                                                <line x1="0" y1="3" x2="6" y2="3" stroke="#333" strokeWidth="1" />
                                                            </pattern>

                                                            <pattern id="triangles" width="10" height="10" patternUnits="userSpaceOnUse">
                                                                <rect width="10" height="10" fill="#FFDEAD" />
                                                                <polygon points="5,0 10,10 0,10" fill="#333" />
                                                            </pattern>

                                                            <pattern id="dots-small" width="4" height="4" patternUnits="userSpaceOnUse">
                                                                <rect width="4" height="4" fill="#D8BFD8" />
                                                                <circle cx="2" cy="2" r="0.5" fill="#333" />
                                                            </pattern>

                                                            <linearGradient id="multiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                                <stop offset="0%" stopColor="#FFB6C1" />
                                                                <stop offset="50%" stopColor="#ADD8E6" />
                                                                <stop offset="100%" stopColor="#90EE90" />
                                                            </linearGradient>

                                                            <pattern id="colorStripes" width="6" height="6" patternUnits="userSpaceOnUse">
                                                                <rect width="6" height="3" fill="#FFD700" />
                                                                <rect y="3" width="6" height="3" fill="#87CEFA" />
                                                            </pattern>

                                                        </defs>
                                                        {question?.pattern.map((p, i) => ( 
                                                            <>
                                                                {p != null ? (
                                                                    <g key={i}>
                                                                        <g dangerouslySetInnerHTML={{ __html: p || "?" }} />
                                                                    </g>
                                                                ) : (
                                                                    <text x={xCord} y={yCord} fontSize="24" textAnchor="middle" fill="#555" dominantBaseline="middle" alignmentBaseline="middle">?</text>
                                                                )}
                                                            </>
                                                        ))}

                                                    </svg>
                                                </>
                                            )}
                                        </>
                                    ) : <></>}
                                </div>
                            </IonText>

                            <IonRadioGroup className={(question.type != "figure" && question.type != "repeating") ? "" : "displayFlex"}
                                value={selected}
                                onIonChange={(e) => handleAnswer(e.detail.value)}
                            >
                                {question.options.map((option, idx) => (
                                    <IonItem
                                        key={idx}
                                        className={`mygrid grid-item ${selected === option
                                            ? option === question.correctAnswer
                                                ? "correct"
                                                : "errorOptions"
                                            : ""
                                            }`}
                                    >
                                        <IonLabel style={ (question.type !== "figure" && question.type !== "repeating") ? {} : { width: "auto" } }>
                                            {question.type === "repeating" ? (
                                                <img
                                                    src={`${process.env.REACT_APP_API_URL ?? ""}${option}`}
                                                    style={{ width: "50px" }}
                                                    alt="option-img"
                                                />
                                            ) : question.type === "figure" ? (
                                                <>
                                                    {question.subtype === "rectangle" ? (
                                                        <span dangerouslySetInnerHTML={{ __html: option as string }} />
                                                    ) : (
                                                        <svg width="120" height="120">
                                                            <g dangerouslySetInnerHTML={{ __html: option as string }} />
                                                        </svg>
                                                    )}
                                                </>
                                            ) : (
                                                <span>{option}</span>
                                            )}
                                        </IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK ?? ""}
                        alt="Well Done"
                    />
                </div>
            )}
        </>
    );
};

export default AutoPatternGenerator;